// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, useState } from 'react'

interface DebugIndicatorProps {
  storageKey?: string
  label?: string
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
}

export function DebugIndicator({
  storageKey = 'debugModeEnabled',
  label = 'Debug Mode Active',
  position = 'bottom-right',
}: DebugIndicatorProps) {
  const [isDebugMode, setIsDebugMode] = useState(false)

  useEffect(() => {
    const debugModeEnabled = sessionStorage.getItem(storageKey) === 'true'
    setIsDebugMode(debugModeEnabled)

    const handleStorageChange = () => {
      const debugModeEnabled = sessionStorage.getItem(storageKey) === 'true'
      setIsDebugMode(debugModeEnabled)
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [storageKey])

  if (!isDebugMode) return null

  const positionClasses = {
    'bottom-right': 'bottom-2.5 right-2.5',
    'bottom-left': 'bottom-2.5 left-2.5',
    'top-right': 'top-2.5 right-2.5',
    'top-left': 'top-2.5 left-2.5',
  }

  return (
    <div
      className={`fixed ${positionClasses[position]} px-2.5 py-1.5 bg-orange-500/80 text-white rounded text-xs z-[9999] pointer-events-none`}
      role="status"
      aria-live="polite"
    >
      {label}
    </div>
  )
}
